/**
 * The POS on the device: the catalog it sells from, the reference numbers it gives, and
 * the queue of what it has done, sent to the server whenever there is a connection.
 *
 * Nothing here waits for the network. A sale is complete the moment it is made: it has
 * its reference and its place in the queue, and it is sent when it can be. The screen
 * reads the queue's state (online or not, how many waiting, anything refused) and shows
 * it, so nobody at the booth has to wonder whether a sale "went through".
 */

import { buildReference, ReferenceKind } from "@ohc/shared";
import { useSyncExternalStore } from "react";
import { read, update, write } from "./store";

// ---------------------------------------------------------------------------
// What the server sends and takes
// ---------------------------------------------------------------------------

export interface CatalogProduct {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly category: string;
  /** Pokemon, Lorcana, Riftbound: how a customer asks for a thing. */
  readonly game: string | null;
  readonly setName: string | null;
  readonly language: string | null;
  readonly itemType: string;
  readonly priceCents: number | null;
  /**
   * What one of these costs the shop, and how that was worked out. A sale below it loses
   * money, which is the one thing somebody haggling at a table has to know.
   */
  readonly costCents?: number | null;
  readonly costNote?: string | null;
  readonly priceSetOn: string | null;
  /** FALSE for raw singles and the like: the amount is typed and no stock moves. */
  readonly tracksStock: boolean;
  readonly onHand: number;
  /** Kept at the top of its shelf, whatever it sells. */
  readonly pinned?: boolean;
  /** Where it sits inside its category: Packs, Bundles and so on. */
  readonly section?: string | null;
  /** Held and not sold: shown greyed, and never put in a basket. */
  readonly notForSale?: boolean;
  /** Its picture, for the tile. */
  readonly photoId?: string | null;
  /** How many the booth sold in the last month, which is how a shelf orders itself. */
  readonly soldLately?: number;
}

export interface CatalogSlab {
  readonly lotId: string;
  readonly cardName: string | null;
  readonly certNo: string | null;
  readonly grader: string | null;
  readonly grade: string | null;
  readonly setName: string | null;
  readonly priceCents: number | null;
  readonly priceSetOn: string | null;
  readonly location: string | null;
  /** The slab's own photo, and where its cert number is covered, when it is. */
  readonly photoId?: string | null;
  readonly certCover?: { readonly left: number; readonly top: number; readonly right: number; readonly bottom: number } | null;
}

/** Someone who can sell, and whether they may allow a void or a correction. */
export interface StaffMember {
  readonly id: string;
  readonly name: string;
  readonly canVoid: boolean;
}

export interface Catalog {
  readonly device: { code: string; name: string; cashAccountId: string | null; paynowAccountId: string | null };
  readonly today: string;
  readonly lastSequence: { sale: number; buyback: number };
  readonly products: CatalogProduct[];
  readonly slabs: CatalogSlab[];
  readonly locations: { id: string; name: string; kind: string }[];
  readonly accounts: { id: string; name: string; kind: string }[];
  readonly staff: StaffMember[];
  /**
   * What a supervisor has said about this iPad's sales: waiting, allowed (the booth may
   * finish the correction without a PIN) or refused, with why.
   */
  readonly flags?: SaleFlagState[];
  /** When this copy was taken, for "prices as of" at the booth. */
  readonly fetchedAt: string;
}

export interface SaleFlagState {
  readonly saleId: string;
  readonly status: "open" | "allowed" | "refused";
  readonly note: string;
  readonly decidedBy: { id: string; name: string } | null;
  readonly decisionNote: string | null;
}

export interface SaleLine {
  readonly lineId: string;
  readonly productId?: string | null;
  readonly slabLotId?: string | null;
  readonly quantity: number;
  readonly unitPriceCents: number;
  /** For the screen and the receipt; the server takes its own from the product. */
  readonly description: string;
  /** A few words the server keeps on an uncounted line, such as how many cards. */
  readonly note?: string;
  /**
   * Photographs of something the shop is selling that is not on the system: taken at the
   * table, held on the line until the sale has a number, then sent with the sale. They
   * never go in the sale itself, which has to stay small enough to send over a show's
   * connection.
   */
  readonly photos?: readonly { readonly contentType: string; readonly data: string }[];
}

export type Operation =
  | {
      readonly opId: string;
      readonly type: "sale.create";
      readonly saleId: string;
      readonly reference: string;
      readonly soldAt: string;
      readonly soldBy: string;
      readonly locationId?: string | null;
      readonly event?: string | null;
      readonly customerName?: string | null;
      readonly discountCents?: number;
      readonly surchargeCents?: number;
      readonly lines: readonly Omit<SaleLine, "description" | "photos">[];
    }
  | {
      readonly opId: string;
      readonly type: "sale.pay";
      readonly saleId: string;
      readonly method: "cash" | "paynow";
      readonly accountId?: string | null;
      readonly paidAt: string;
      readonly confirmedBy: string;
      /** The PayNow payment picked on the screen, from the bank's alerts. */
      readonly receiptId?: string | null;
      /** Paid partly in cash, the rest by PayNow (D, 18 October 2026). */
      readonly cashPartCents?: number | null;
      readonly cashAccountId?: string | null;
    }
  | {
      readonly opId: string;
      readonly type: "buyback.create";
      readonly buybackId: string;
      readonly reference: string;
      readonly boughtAt: string;
      readonly boughtBy: string;
      readonly locationId?: string | null;
      readonly event?: string | null;
      readonly sellerName: string;
      readonly sellerPhone?: string | null;
      readonly description: string;
      readonly note?: string | null;
      readonly amountCents: number;
      readonly method: "cash" | "paynow" | "later";
      readonly accountId?: string | null;
    }
  | {
      readonly opId: string;
      readonly type: "stock.count";
      readonly countId: string;
      readonly countedAt: string;
      readonly countedBy: string;
      readonly locationId?: string | null;
      readonly note?: string | null;
      readonly lines: readonly {
        readonly productId?: string | null;
        readonly slabLotId?: string | null;
        readonly countedQuantity: number;
      }[];
    }
  | {
      readonly opId: string;
      readonly type: "sale.void";
      readonly saleId: string;
      readonly reason: string;
      readonly by: string;
      readonly at: string;
    }
  | {
      /** A sale made wrong, taken back because the corrected one replaces it. */
      readonly opId: string;
      readonly type: "sale.correct";
      readonly saleId: string;
      readonly replacedBy: string;
      readonly by: string;
      readonly at: string;
    }
  | {
      /** A sale the booth asks a supervisor about, with what is wrong. */
      readonly opId: string;
      readonly type: "sale.flag";
      readonly flagId: string;
      readonly saleId: string;
      readonly note: string;
      readonly by: string;
      readonly at: string;
    };

type Result =
  | { readonly opId: string; readonly status: "applied"; readonly detail?: string }
  | { readonly opId: string; readonly status: "refused"; readonly error: string };

/** A sale as this device remembers it, for today's list at the booth. */
export interface LocalBuyback {
  readonly id: string;
  readonly reference: string;
  readonly boughtAt: string;
  readonly boughtByName: string;
  readonly sellerName: string;
  readonly sellerPhone: string | null;
  readonly description: string;
  readonly amountCents: number;
  readonly method: "cash" | "paynow" | "later";
  readonly photos: number;
  readonly note: string | null;
  readonly refused: boolean;
}

export interface LocalSale {
  readonly id: string;
  readonly reference: string;
  readonly soldAt: string;
  readonly soldBy: string;
  readonly soldByName: string;
  readonly lines: readonly SaleLine[];
  readonly totalCents: number;
  /** "corrected" is a sale put right at the booth: taken back, and replaced by another. */
  readonly status: "unpaid" | "paid" | "void" | "corrected";
  readonly method: "cash" | "paynow" | null;
  /** Something the server said about it: a slab held, stock below zero, a refusal. */
  readonly note: string | null;
  readonly refused: boolean;
  /** What the booth told a supervisor was wrong with it, once it has asked. */
  readonly asked?: string | null;
}

// ---------------------------------------------------------------------------
// Ids and times
// ---------------------------------------------------------------------------

const CROCKFORD = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/** A ULID: sortable by time, unique without asking the server. */
export function newId(): string {
  let time = Date.now();
  let head = "";
  for (let i = 0; i < 10; i++) {
    head = CROCKFORD.charAt(time % 32) + head;
    time = Math.floor(time / 32);
  }
  const random = crypto.getRandomValues(new Uint8Array(16));
  let tail = "";
  for (const byte of random) tail += CROCKFORD.charAt(byte % 32);
  return head + tail;
}

function singaporeToday(): { year: number; month: number; day: number; iso: string } {
  const iso = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore" }).format(new Date());
  const [year, month, day] = iso.split("-").map(Number) as [number, number, number];
  return { year, month, day, iso };
}

// ---------------------------------------------------------------------------
// The catalog
// ---------------------------------------------------------------------------

/**
 * The catalog: fresh from the server when there is a connection, and kept, so that
 * with none the iPad sells from the last copy it had.
 */
export async function loadCatalog(): Promise<{ catalog: Catalog | undefined; fresh: boolean; error?: string }> {
  try {
    const response = await fetch("/api/pos/catalog", { credentials: "same-origin" });
    const body = (await response.json()) as Catalog | { error?: string };
    if (!response.ok) {
      const stored = await read<Catalog>("catalog");
      return { catalog: stored, fresh: false, error: "error" in body && body.error ? body.error : "The product catalogue did not load" };
    }
    const catalog = { ...(body as Catalog), fetchedAt: new Date().toISOString() };
    await write("catalog", catalog);
    // What the server has seen from this device today is the floor for the next number.
    await update<number>(`seq:S:${catalog.today}`, (current) => Math.max(current ?? 0, catalog.lastSequence.sale));
    await update<number>(`seq:B:${catalog.today}`, (current) => Math.max(current ?? 0, catalog.lastSequence.buyback));
    return { catalog, fresh: true };
  } catch {
    return { catalog: await read<Catalog>("catalog"), fresh: false };
  }
}

/** Take what was just sold off the device's copy, so the next customer sees what is left. */
async function takeFromCatalog(lines: readonly SaleLine[], sign: 1 | -1): Promise<void> {
  await update<Catalog | undefined>("catalog", (catalog) => {
    if (!catalog) return catalog;
    const sold = new Map<string, number>();
    for (const line of lines) if (line.productId) sold.set(line.productId, (sold.get(line.productId) ?? 0) + line.quantity);
    const slabsGone = new Set(lines.filter((line) => line.slabLotId).map((line) => line.slabLotId));
    return {
      ...catalog,
      products: catalog.products.map((product) =>
        sold.has(product.id) ? { ...product, onHand: product.onHand - sign * (sold.get(product.id) ?? 0) } : product,
      ),
      slabs: sign === 1 ? catalog.slabs.filter((slab) => !slabsGone.has(slab.lotId)) : catalog.slabs,
    };
  });
}

// ---------------------------------------------------------------------------
// Reference numbers
// ---------------------------------------------------------------------------

/**
 * The next sale reference for this device today: S26A09A07. The counter lives on the
 * device and never goes back, and the server's own record of this device's numbers is
 * its floor, so an iPad that was reset carries on where it left off.
 */
export async function nextSaleReference(deviceCode: string): Promise<string> {
  const today = singaporeToday();
  const sequence = await update<number>(`seq:S:${today.iso}`, (current) => (current ?? 0) + 1);
  return buildReference({
    kind: ReferenceKind.Sale,
    date: { year: today.year, month: today.month, day: today.day },
    device: deviceCode,
    sequence,
  });
}

/** The next buyback reference for this device today: B26A09A02. The same counter, kept apart. */
export async function nextBuybackReference(deviceCode: string): Promise<string> {
  const today = singaporeToday();
  const sequence = await update<number>(`seq:B:${today.iso}`, (current) => (current ?? 0) + 1);
  return buildReference({
    kind: ReferenceKind.Buyback,
    date: { year: today.year, month: today.month, day: today.day },
    device: deviceCode,
    sequence,
  });
}

// ---------------------------------------------------------------------------
// Today's sales on this device
// ---------------------------------------------------------------------------

function salesKey(): string {
  return `sales:${singaporeToday().iso}`;
}

export async function todaysSales(): Promise<LocalSale[]> {
  return (await read<LocalSale[]>(salesKey())) ?? [];
}

/** How far back a sale can still be found on the iPad: a correction allowed late is finished days later. */
export const KEPT_DAYS = 14;

/** The keys of this iPad's sales for today and the days before it, newest first. */
function recentKeys(days: number = KEPT_DAYS): string[] {
  const now = Date.now();
  const day = (at: number): string => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore" }).format(new Date(at));
  return [...new Set(Array.from({ length: days + 1 }, (_, back) => `sales:${day(now - back * 86_400_000)}`))];
}

/** The Singapore day so many days back, as YYYY-MM-DD: 1 is yesterday. */
export function dayBack(back: number): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore" }).format(new Date(Date.now() - back * 86_400_000));
}

/** This iPad's sales of one day, so many days back: 1 is yesterday. */
export async function salesDaysBack(back: number): Promise<LocalSale[]> {
  return (await read<LocalSale[]>(`sales:${dayBack(back)}`)) ?? [];
}

/** This iPad's sales of the last fortnight, for the ones a supervisor allowed to be put right. */
export async function recentSales(): Promise<LocalSale[]> {
  const all: LocalSale[] = [];
  for (const key of recentKeys()) all.push(...((await read<LocalSale[]>(key)) ?? []));
  return all;
}

/** Change a sale wherever it is kept: today's, or an earlier day's still on the iPad. */
async function changeSale(id: string, change: (sale: LocalSale) => LocalSale): Promise<void> {
  for (const key of recentKeys()) {
    const sales = (await read<LocalSale[]>(key)) ?? [];
    if (!sales.some((sale) => sale.id === id)) continue;
    await update<LocalSale[]>(key, (current) => (current ?? []).map((sale) => (sale.id === id ? change(sale) : sale)));
    return;
  }
}

// ---------------------------------------------------------------------------
// The queue
// ---------------------------------------------------------------------------

export interface QueueState {
  readonly online: boolean;
  readonly waiting: number;
  readonly sending: boolean;
  readonly lastSentAt: string | null;
  /** The newest thing the server would not take, shown until it is looked at. */
  readonly problem: string | null;
  /** Bumps whenever today's sales change, so lists reload. */
  readonly revision: number;
}

let state: QueueState = {
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  waiting: 0,
  sending: false,
  lastSentAt: null,
  problem: null,
  revision: 0,
};
const listeners = new Set<() => void>();

function setState(change: Partial<QueueState>): void {
  state = { ...state, ...change };
  for (const listener of listeners) listener();
}

export function useQueue(): QueueState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}

export function dismissProblem(): void {
  setState({ problem: null });
}

async function enqueue(operations: readonly Operation[]): Promise<void> {
  const queue = await update<Operation[]>("outbox", (current) => [...(current ?? []), ...operations]);
  setState({ waiting: queue.length, revision: state.revision + 1 });
  void flush();
}

let flushing: Promise<void> | undefined;

/** Whether the server answers, for a till that has nothing to send and thinks it is offline. */
async function probe(): Promise<void> {
  try {
    const response = await fetch("/api/health", { cache: "no-store", credentials: "same-origin" });
    if (response.ok) setState({ online: true });
  } catch {
    // Still no connection.
  }
}

/**
 * Send what is waiting, oldest first, fifty at a time, until nothing is left or the
 * connection drops. An operation leaves the queue only once the server has answered it,
 * applied or refused; with no answer it stays and goes again next time.
 */
export function flush(): Promise<void> {
  flushing ??= (async () => {
    setState({ sending: true });
    try {
      // Fifty at a time, or one at a time while a bad one is being looked for.
      let batchSize = 50;
      for (;;) {
        const queue = (await read<Operation[]>("outbox")) ?? [];
        setState({ waiting: queue.length });
        if (queue.length === 0) {
          // Nothing to send says nothing about the connection, so a till marked offline by
          // one dropped request would say Offline all afternoon while it sold quietly. It
          // asks the server instead.
          if (!state.online) await probe();
          break;
        }
        const batch = queue.slice(0, batchSize);

        let results: Result[];
        try {
          const response = await fetch("/api/pos/sync", {
            method: "POST",
            credentials: "same-origin",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ operations: batch }),
          });
          if (!response.ok) {
            const body = (await response.json().catch(() => ({}))) as { error?: string };
            /*
             * A 400 is the server saying it cannot read what was sent, and sending the same
             * thing again will get the same answer for ever. One sale the server will never
             * take would otherwise sit at the front of the queue and hold everything behind
             * it on the iPad, which at a show is a day's takings.
             *
             * So a batch of one that is refused like that is put aside: the sale stays on
             * the iPad, marked and named on the screen, and everything after it goes. A
             * bigger batch is split instead, because the bad one has to be found first.
             */
            if (response.status === 400) {
              if (batch.length === 1) {
                const bad = batch[0];
                const why = body.error ?? "The server could not read this transaction";
                setState({ online: true, problem: `${why}. It is held on this iPad; the other transactions are still uploading.` });
                if (bad) {
                  await update<Operation[]>("outbox", (current) =>
                    (current ?? []).filter((operation) => operation.opId !== bad.opId),
                  );
                  await update<Operation[]>("held", (current) => [...(current ?? []), bad]);
                  // A question that could not be asked leaves the sale itself as it was.
                  if (bad.type === "sale.flag") await changeSale(bad.saleId, (sale) => ({ ...sale, asked: null, note: why }));
                  else if ("saleId" in bad) await changeSale(bad.saleId, (sale) => ({ ...sale, refused: true, note: why }));
                  if ("buybackId" in bad) {
                    await changeBuyback(bad.buybackId, (buyback) => ({ ...buyback, refused: true, note: why }));
                  }
                }
                continue;
              }
              batchSize = 1;
              continue;
            }
            // Signed out, or the device not set up: keep everything, say why, stop.
            setState({ online: true, problem: body.error ?? `The server did not accept the transactions (${response.status})` });
            break;
          }
          results = ((await response.json()) as { results: Result[] }).results;
          batchSize = 50;
          setState({ online: true });
        } catch {
          setState({ online: false });
          break;
        }

        const answered = new Set(results.map((result) => result.opId));
        await update<Operation[]>("outbox", (current) => (current ?? []).filter((operation) => !answered.has(operation.opId)));
        for (const result of results) {
          const operation = batch.find((entry) => entry.opId === result.opId);
          if (!operation) continue;
          if (result.status === "refused") {
            setState({ problem: result.error });
            if (operation.type === "sale.flag") {
              await changeSale(operation.saleId, (sale) => ({ ...sale, asked: null, note: result.error }));
            } else if ("saleId" in operation) {
              await changeSale(operation.saleId, (sale) => ({ ...sale, refused: true, note: result.error }));
            }
            if ("buybackId" in operation) {
              await changeBuyback(operation.buybackId, (buyback) => ({ ...buyback, refused: true, note: result.error }));
            }
          } else if (result.detail && operation.type === "sale.create") {
            await changeSale(operation.saleId, (sale) => ({ ...sale, note: result.detail ?? null }));
          }
        }
        setState({ lastSentAt: new Date().toISOString(), revision: state.revision + 1 });
        if (results.length < batch.length) break;
      }
      await sendPhotos();
    } finally {
      flushing = undefined;
      const left = ((await read<Operation[]>("outbox")) ?? []).length;
      setState({ sending: false, waiting: left });
    }
  })();
  return flushing;
}

// ---------------------------------------------------------------------------
// The photographs
// ---------------------------------------------------------------------------

interface QueuedPhoto {
  readonly photoId: string;
  /** What it is a photograph of. Photos queued by an older release carry none, and the
   * server reads those as a buyback, which is all there was. */
  readonly subject?: "buyback" | "sale";
  readonly subjectId: string;
  readonly contentType: string;
  readonly takenAt: string;
  /** The picture, base64, as it came off the camera and was shrunk. */
  readonly data: string;
}

/**
 * Keep a photo on the device and send it later.
 *
 * Photos go after the operations, because the server will not take a photo of a buyback
 * it has not seen yet. One at a time: a photo is a megabyte or so, and a booth on a phone
 * connection does better with small requests than with one large one.
 */
async function queuePhotos(photos: readonly QueuedPhoto[]): Promise<void> {
  if (photos.length === 0) return;
  await update<QueuedPhoto[]>("photos", (current) => [...(current ?? []), ...photos]);
}

async function sendPhotos(): Promise<void> {
  for (;;) {
    const queue = (await read<QueuedPhoto[]>("photos")) ?? [];
    const photo = queue[0];
    if (!photo) return;

    let keep = false;
    try {
      const response = await fetch("/api/pos/photos", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(photo),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { error?: string };
        /*
         * What was sold or bought is not on the server yet, or the person has been signed
         * out, or the server is having a moment: in every one of those the photograph is
         * still good and will go next time. Only a 400 that is about the picture itself,
         * such as one too big to take, is thrown away, because sending it again would get
         * the same answer for ever and hold up every photo behind it.
         */
        keep = response.status !== 400 || (body.error ?? "").includes("not in yet");
        // Waiting for the sale to land is the ordinary case and says nothing. Waiting on a
        // server or a sign in is worth a word, and a photo given up on certainly is.
        if (!keep) {
          setState({ problem: body.error ?? `A photo could not be uploaded and has been discarded (${response.status})` });
        } else if (response.status !== 400) {
          setState({ problem: body.error ?? `Photos are waiting to upload (${response.status})` });
        }
      }
    } catch {
      setState({ online: false });
      return;
    }
    if (keep) return;
    await update<QueuedPhoto[]>("photos", (current) => (current ?? []).filter((entry) => entry.photoId !== photo.photoId));
    setState({ revision: state.revision + 1 });
  }
}

/** The photos of one buyback or sale still on this device, ready to show (D, 9 October 2026). */
export async function photosOnDevice(subjectId: string): Promise<string[]> {
  return ((await read<QueuedPhoto[]>("photos")) ?? [])
    .filter((photo) => photo.subjectId === subjectId)
    .map((photo) => `data:${photo.contentType};base64,${photo.data}`);
}

/** How many photos are still on the device waiting to be sent. */
export async function photosWaiting(): Promise<number> {
  return ((await read<QueuedPhoto[]>("photos")) ?? []).length;
}

// ---------------------------------------------------------------------------
// Buying at the booth
// ---------------------------------------------------------------------------

function buybacksKey(): string {
  return `buybacks:${singaporeToday().iso}`;
}

export async function todaysBuybacks(): Promise<LocalBuyback[]> {
  return (await read<LocalBuyback[]>(buybacksKey())) ?? [];
}

async function changeBuyback(id: string, change: (buyback: LocalBuyback) => LocalBuyback): Promise<void> {
  await update<LocalBuyback[]>(buybacksKey(), (rows) =>
    (rows ?? []).map((buyback) => (buyback.id === id ? change(buyback) : buyback)),
  );
}

/**
 * Record what was bought at the booth: the price, who it came from, and the photographs.
 *
 * The buyback is complete on the device the moment it is made, like a sale. What the
 * cards actually are is worked out at the desk afterwards, from these photos.
 */
export async function recordBuyback(input: {
  readonly deviceCode: string;
  readonly buyer: { id: string; name: string };
  readonly locationId: string | null;
  readonly event: string | null;
  readonly sellerName: string;
  readonly sellerPhone: string | null;
  readonly description: string;
  readonly note: string | null;
  readonly amountCents: number;
  readonly method: "cash" | "paynow" | "later";
  readonly accountId: string | null;
  readonly photos: readonly { contentType: string; data: string }[];
}): Promise<LocalBuyback> {
  const now = new Date().toISOString();
  const id = newId();
  const reference = await nextBuybackReference(input.deviceCode);

  const buyback: LocalBuyback = {
    id,
    reference,
    boughtAt: now,
    boughtByName: input.buyer.name,
    sellerName: input.sellerName,
    sellerPhone: input.sellerPhone,
    description: input.description,
    amountCents: input.amountCents,
    method: input.method,
    photos: input.photos.length,
    note: null,
    refused: false,
  };
  await update<LocalBuyback[]>(buybacksKey(), (rows) => [buyback, ...(rows ?? [])]);
  await queuePhotos(
    input.photos.map((photo) => ({
      photoId: newId(),
      subject: "buyback" as const,
      subjectId: id,
      contentType: photo.contentType,
      takenAt: now,
      data: photo.data,
    })),
  );
  await enqueue([
    {
      opId: newId(),
      type: "buyback.create",
      buybackId: id,
      reference,
      boughtAt: now,
      boughtBy: input.buyer.id,
      locationId: input.locationId,
      event: input.event,
      sellerName: input.sellerName,
      sellerPhone: input.sellerPhone,
      description: input.description,
      note: input.note,
      amountCents: input.amountCents,
      method: input.method,
      accountId: input.accountId,
    },
  ]);
  return buyback;
}

/**
 * A count of what is on the table, kept on the iPad and sent as one thing.
 *
 * The catalog on the device is put right at the same time, so the tiles show what was
 * counted straight away rather than what the server last said.
 */
export async function recordCount(input: {
  readonly counter: { id: string; name: string };
  readonly locationId: string | null;
  readonly note: string | null;
  readonly lines: readonly { productId?: string; slabLotId?: string; countedQuantity: number }[];
}): Promise<void> {
  await enqueue([
    {
      opId: newId(),
      type: "stock.count",
      countId: newId(),
      countedAt: new Date().toISOString(),
      countedBy: input.counter.id,
      locationId: input.locationId,
      note: input.note,
      lines: input.lines,
    },
  ]);

  await update<Catalog | undefined>("catalog", (catalog) => {
    if (!catalog) return catalog;
    const counted = new Map(
      input.lines.filter((line) => line.productId).map((line) => [line.productId as string, line.countedQuantity]),
    );
    return {
      ...catalog,
      products: catalog.products.map((product) =>
        counted.has(product.id) ? { ...product, onHand: counted.get(product.id) ?? product.onHand } : product,
      ),
    };
  });
}

/** Start sending on its own: now, when the connection comes back, and every 20 seconds. */
export function startQueue(): () => void {
  void read<Operation[]>("outbox").then((queue) => setState({ waiting: (queue ?? []).length }));
  void flush();
  const online = (): void => {
    setState({ online: true });
    void flush();
  };
  const offline = (): void => setState({ online: false });
  window.addEventListener("online", online);
  window.addEventListener("offline", offline);
  const timer = window.setInterval(() => void flush(), 20_000);
  return () => {
    window.removeEventListener("online", online);
    window.removeEventListener("offline", offline);
    window.clearInterval(timer);
  };
}

// ---------------------------------------------------------------------------
// What the screen does
// ---------------------------------------------------------------------------

export interface Checkout {
  readonly reference: string;
  readonly saleId: string;
}

/** A reference for the basket, shown before payment so it can go into OneCollect. */
export async function beginCheckout(deviceCode: string): Promise<Checkout> {
  return { reference: await nextSaleReference(deviceCode), saleId: newId() };
}

/**
 * Record the sale. Paid now: the sale and its payment, one after the other. Paid later:
 * the sale alone, as an invoice waiting for its money.
 */
export async function recordSale(input: {
  readonly checkout: Checkout;
  readonly lines: readonly SaleLine[];
  readonly seller: { id: string; name: string };
  readonly locationId: string | null;
  readonly event: string | null;
  readonly customerName: string | null;
  /** Taken off the whole sale at the table, in cents. */
  readonly discountCents?: number;
  /** Added on top of the whole sale at the table, in cents. Never with a discount. */
  readonly surchargeCents?: number;
  readonly payment: { method: "cash" | "paynow"; accountId: string | null; receiptId?: string | null; cashPartCents?: number | null; cashAccountId?: string | null } | null;
  /**
   * The sale this one puts right, and who allowed it. That sale is taken back before this
   * one goes, and each says so: the old one who corrected it and what replaced it, this
   * one what it corrects.
   */
  readonly corrects?: { readonly sale: LocalSale; readonly by: { id: string; name: string } };
}): Promise<LocalSale> {
  const now = new Date().toISOString();
  const subtotal = input.lines.reduce((sum, line) => sum + line.quantity * line.unitPriceCents, 0);
  const discountCents = Math.max(0, Math.min(Math.round(input.discountCents ?? 0), subtotal));
  const surchargeCents = discountCents > 0 ? 0 : Math.max(0, Math.round(input.surchargeCents ?? 0));
  const totalCents = subtotal - discountCents + surchargeCents;
  const sale: LocalSale = {
    id: input.checkout.saleId,
    reference: input.checkout.reference,
    soldAt: now,
    soldBy: input.seller.id,
    soldByName: input.seller.name,
    lines: input.lines,
    totalCents,
    status: input.payment ? "paid" : "unpaid",
    method: input.payment?.method ?? null,
    note: input.corrects ? `Correction of ${input.corrects.sale.reference}` : null,
    refused: false,
  };

  // The one it replaces goes first: its stock comes back to the shelf here and now, and
  // the server hears that it was taken back before it hears about its replacement.
  if (input.corrects) {
    const { sale: wrong, by } = input.corrects;
    await changeSale(wrong.id, (current) => ({
      ...current,
      status: "corrected",
      note: `Corrected by ${by.name}, replaced by ${sale.reference}`,
    }));
    await takeFromCatalog(wrong.lines, -1);
    await enqueue([
      { opId: newId(), type: "sale.correct", saleId: wrong.id, replacedBy: sale.reference, by: by.id, at: now },
    ]);
  }

  await update<LocalSale[]>(salesKey(), (sales) => [sale, ...(sales ?? [])]);
  await takeFromCatalog(input.lines, 1);

  // What the booth photographed at the table, kept with the sale's own number so the desk
  // can see what was sold when the line only says what it cost.
  await queuePhotos(
    input.lines.flatMap((line) =>
      (line.photos ?? []).map((photo) => ({
        photoId: newId(),
        subject: "sale" as const,
        subjectId: sale.id,
        contentType: photo.contentType,
        takenAt: now,
        data: photo.data,
      })),
    ),
  );

  const operations: Operation[] = [
    {
      opId: newId(),
      type: "sale.create",
      saleId: sale.id,
      reference: sale.reference,
      soldAt: now,
      soldBy: input.seller.id,
      locationId: input.locationId,
      event: input.event,
      customerName: input.customerName,
      ...(discountCents > 0 ? { discountCents } : {}),
      ...(surchargeCents > 0 ? { surchargeCents } : {}),
      // Without the words on the screen and without the pictures: the server writes its
      // own description, and the pictures go one at a time behind this.
      lines: input.lines.map(({ description: _description, photos: _photos, ...line }) => line),
    },
  ];
  if (input.payment) {
    operations.push({
      opId: newId(),
      type: "sale.pay",
      saleId: sale.id,
      method: input.payment.method,
      accountId: input.payment.accountId,
      paidAt: now,
      confirmedBy: input.seller.id,
      ...(input.payment.receiptId ? { receiptId: input.payment.receiptId } : {}),
      ...(input.payment.cashPartCents ? { cashPartCents: input.payment.cashPartCents, cashAccountId: input.payment.cashAccountId ?? null } : {}),
    });
  }
  await enqueue(operations);
  return sale;
}

/**
 * The account this till's cash or PayNow goes into: the one set for the iPad, or, when the
 * company has only one account it could be, that one. Null while somebody has to choose,
 * which is when the shop has two bank accounts and nobody has said which takes PayNow.
 */
export function tillAccount(catalog: Catalog, method: "cash" | "paynow"): string | null {
  const set = method === "cash" ? catalog.device.cashAccountId : catalog.device.paynowAccountId;
  if (set) return set;
  const choices = catalog.accounts.filter((account) => account.kind === (method === "cash" ? "cash" : "bank"));
  return choices.length === 1 ? (choices[0]?.id ?? null) : null;
}

export async function payLater(sale: LocalSale, method: "cash" | "paynow", accountId: string | null, by: string): Promise<void> {
  await changeSale(sale.id, (current) => ({ ...current, status: "paid", method }));
  await enqueue([
    { opId: newId(), type: "sale.pay", saleId: sale.id, method, accountId, paidAt: new Date().toISOString(), confirmedBy: by },
  ]);
}

/**
 * Ask a supervisor about a sale, with what is wrong. It goes in the queue like a sale, so
 * it is asked even with no signal; the answer comes back with the next stock list.
 */
export async function flagSale(sale: LocalSale, note: string, by: string): Promise<void> {
  const said = note.replace(/\s+/g, " ").trim().slice(0, 300);
  await changeSale(sale.id, (current) => ({ ...current, asked: said }));
  await enqueue([
    { opId: newId(), type: "sale.flag", flagId: newId(), saleId: sale.id, note: said, by, at: new Date().toISOString() },
  ]);
}

export async function voidSale(sale: LocalSale, reason: string, by: string): Promise<void> {
  await changeSale(sale.id, (current) => ({ ...current, status: "void" }));
  await takeFromCatalog(sale.lines, -1);
  await enqueue([{ opId: newId(), type: "sale.void", saleId: sale.id, reason, by, at: new Date().toISOString() }]);
}

